import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Receipt, Table, Checks, Export, UsersThree, Bell, SignOut, List, X, FolderSimple, SquaresFour } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useAuth } from "../context/AuthContext";
import { useT, LangToggle } from "../i18n";
import { api, errMsg } from "../lib/api";

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-2.5 text-sm font-medium border-l-2 transition-colors duration-150 ${
    isActive ? "border-slate-900 bg-white text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900 hover:bg-white"
  }`;

export const Layout = ({ children, workspace }) => {
  const { user, logout } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const isManager = ["admin", "manager"].includes(user?.role);
  const isTeam = workspace?.type === "team";
  const unread = notifications.filter((n) => !n.read).length;

  useEffect(() => {
    const load = () => api.get("/notifications").then((r) => setNotifications(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const openNotifs = async (o) => {
    setOpen(o);
    if (o && unread > 0) {
      await api.post("/notifications/read");
      setNotifications((ns) => ns.map((n) => ({ ...n, read: true })));
    }
  };

  const notifText = (n) => {
    if (!n.key) return n.message;
    const tpl = t(n.key);
    if (tpl === n.key) return n.message;
    const p = { ...(n.params || {}) };
    if (n.key === "nf_exp_status" || n.key === "nf_report_status") p.status = t(`st_${p.status}`);
    if (n.key === "nf_billing_issue") p.statusText = t(`bstat_${p.status}`);
    p.comment = p.comment ? ` — "${p.comment}"` : "";
    return tpl.replace(/\{(\w+)\}/g, (_, k) => (p[k] ?? ""));
  };

  const nav = (
    <nav className="flex-1 py-4 space-y-0.5">
      <NavLink to="/" end className={linkCls} data-testid="nav-expenses" onClick={() => setMobileNav(false)}>
        <Table size={18} /> {t("nav_expenses")}
      </NavLink>
      <NavLink to="/reports" className={linkCls} data-testid="nav-reports" onClick={() => setMobileNav(false)}>
        <FolderSimple size={18} /> {t("nav_reports")}
      </NavLink>
      {isTeam && isManager && (
        <NavLink to="/approvals" className={linkCls} data-testid="nav-approvals" onClick={() => setMobileNav(false)}>
          <Checks size={18} /> {t("nav_approvals")}
        </NavLink>
      )}
      <NavLink to="/export" className={linkCls} data-testid="nav-export" onClick={() => setMobileNav(false)}>
        <Export size={18} /> {t("nav_export")}
      </NavLink>
      {isTeam && isManager && (
        <NavLink to="/team" className={linkCls} data-testid="nav-team" onClick={() => setMobileNav(false)}>
          <UsersThree size={18} /> {t("nav_team")}
        </NavLink>
      )}
      {user?.memberships?.some((m) => m.role === "admin") && (
        <NavLink to="/overview" className={linkCls} data-testid="nav-overview" onClick={() => setMobileNav(false)}>
          <SquaresFour size={18} /> {t("nav_overview")}
        </NavLink>
      )}
    </nav>
  );

  return (
    <div className="min-h-screen bg-white flex">
      <aside className="hidden md:flex w-56 flex-col bg-slate-50 border-r border-slate-200 fixed inset-y-0">
        <div className="px-4 py-5 border-b border-slate-200 flex items-center gap-2">
          <Receipt size={22} weight="bold" className="text-slate-900" />
          <span className="font-heading font-bold tracking-tight text-slate-900">Pellku</span>
        </div>
        <div className="px-4 py-3 border-b border-slate-200">
          <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">{t("workspace")}</p>
          <p className="text-sm font-medium text-slate-800 truncate" data-testid="workspace-name">{workspace?.name}</p>
          <p className="text-xs text-slate-400 capitalize">{workspace?.type === "team" ? t("ob_team") : t("ob_fl")} · {t(`role_${user?.role}`)}</p>
          <button
            data-testid="leave-company-btn"
            onClick={async () => {
              if (!window.confirm(t("leaveConfirm"))) return;
              try {
                await api.post(`/workspaces/${workspace.id}/leave`);
                toast.success(t("toast_left"));
                window.location.reload();
              } catch (e) {
                toast.error(errMsg(e));
              }
            }}
            className="text-[11px] text-slate-400 hover:text-red-600 mt-1.5 underline underline-offset-2 transition-colors duration-150"
          >
            {t("leaveCompany")}
          </button>
        </div>
        {nav}
        <button
          data-testid="logout-btn"
          onClick={async () => { await logout(); navigate("/"); }}
          className="flex items-center gap-3 px-4 py-3 text-sm text-slate-500 hover:text-red-600 border-t border-slate-200 transition-colors duration-150"
        >
          <SignOut size={18} /> {t("signOut")}
        </button>
      </aside>

      {mobileNav && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm" onClick={() => setMobileNav(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-slate-50 border-r border-slate-200 flex flex-col">
            <div className="px-4 py-5 border-b border-slate-200 flex items-center justify-between">
              <span className="font-heading font-bold text-slate-900">Pellku</span>
              <button onClick={() => setMobileNav(false)} data-testid="close-mobile-nav"><X size={20} /></button>
            </div>
            {nav}
            <button data-testid="mobile-logout-btn" onClick={async () => { await logout(); navigate("/"); }} className="flex items-center gap-3 px-4 py-3 text-sm text-slate-500 border-t border-slate-200">
              <SignOut size={18} /> {t("signOut")}
            </button>
          </aside>
        </div>
      )}

      <div className="flex-1 md:ml-56 flex flex-col min-w-0">
        <header className="h-14 border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 sticky top-0 bg-white/90 backdrop-blur z-30">
          <button className="md:hidden" onClick={() => setMobileNav(true)} data-testid="open-mobile-nav"><List size={22} /></button>
          <div className="hidden md:block" />
          <div className="flex items-center gap-4">
            <LangToggle />
            <Popover open={open} onOpenChange={openNotifs}>
              <PopoverTrigger asChild>
                <button data-testid="notifications-btn" className="relative p-1.5 text-slate-500 hover:text-slate-900 transition-colors duration-150">
                  <Bell size={20} />
                  {unread > 0 && (
                    <span data-testid="notifications-badge" className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{unread}</span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0 rounded-md shadow-[0_8px_30px_rgba(0,0,0,0.08)]">
                <div className="px-4 py-2.5 border-b border-slate-100 text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">{t("notifications")}</div>
                <div className="max-h-80 overflow-y-auto" data-testid="notifications-list">
                  {notifications.length === 0 && <p className="px-4 py-6 text-sm text-slate-400 text-center">{t("noNotifications")}</p>}
                  {notifications.map((n) => (
                    <div key={n.id} className="px-4 py-3 border-b border-slate-50 text-sm text-slate-700 leading-snug">
                      {notifText(n)}
                      <p className="text-[11px] text-slate-400 mt-1 font-mono">{new Date(n.created_at).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            <div className="flex items-center gap-2">
              {user?.picture ? (
                <img src={user.picture} alt="" className="h-7 w-7 rounded-full border border-slate-200" />
              ) : (
                <div className="h-7 w-7 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center">{user?.name?.[0]?.toUpperCase()}</div>
              )}
              <span className="hidden sm:block text-sm font-medium text-slate-800" data-testid="user-name">{user?.name}</span>
            </div>
          </div>
        </header>
        <main className="flex-1">{children}</main>
      </div>
    </div>
  );
};
